import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { formatCurrency, formatDate } from "../lib/constants";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Plus, Trash2, Wallet } from "lucide-react";
import { toast } from "sonner";

export default function ExpensesPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", amount: "", category: "Genel", date: new Date().toISOString().slice(0, 10), notes: "" });

  const load = () => api.get("/expenses").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/expenses", { ...form, amount: Number(form.amount) });
      toast.success("Gider eklendi");
      setOpen(false);
      setForm({ title: "", amount: "", category: "Genel", date: new Date().toISOString().slice(0, 10), notes: "" });
      load();
    } catch { toast.error("Eklenemedi"); }
  };

  const del = async (id) => {
    try { await api.delete(`/expenses/${id}`); load(); toast.success("Silindi"); }
    catch { toast.error("Silinemedi"); }
  };

  const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Muhasebe</div>
          <div className="text-2xl font-display font-semibold tracking-tight mt-1">Giderler</div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-expense-btn" className="rounded-full"><Plus className="h-4 w-4 mr-2" /> Yeni Gider</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Yeni Gider Ekle</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="expense-form">
              <div className="space-y-2">
                <Label>Başlık</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="Adobe abonelik, kira, vs." data-testid="expense-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Tutar (₺)</Label>
                  <Input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required data-testid="expense-amount" />
                </div>
                <div className="space-y-2">
                  <Label>Kategori</Label>
                  <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="expense-category" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Tarih</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="expense-date" />
              </div>
              <div className="space-y-2">
                <Label>Not</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="expense-notes" />
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="save-expense-btn" className="rounded-full">Ekle</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-6">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-lg bg-red-500/10 flex items-center justify-center">
            <Wallet className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Toplam Gider</div>
            <div className="text-3xl font-display font-semibold tracking-tight" data-testid="expense-total">{formatCurrency(total)}</div>
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40 hover:bg-muted/40">
              <TableHead>Tarih</TableHead>
              <TableHead>Başlık</TableHead>
              <TableHead>Kategori</TableHead>
              <TableHead>Tutar</TableHead>
              <TableHead>Not</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">Henüz gider yok.</TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.id} data-testid={`expense-row-${r.id}`}>
                <TableCell className="text-sm">{formatDate(r.date)}</TableCell>
                <TableCell className="font-medium">{r.title}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{r.category}</TableCell>
                <TableCell className="font-mono">{formatCurrency(r.amount)}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{r.notes || "-"}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" onClick={() => del(r.id)} data-testid={`delete-expense-${r.id}`}>
                    <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
